import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Aircraft, type WatchlistItem } from './api';

const TOKEN_KEY = 'adsb-tracker.token';
const LIVE_POLL_MS = 10_000;

export type LiveStatus = 'airborne' | 'ground' | 'offline';

export interface WatchlistEntry extends WatchlistItem {
  status: LiveStatus;
  live: Aircraft | null;
}

/**
 * Watchlist-Zustand: Liste vom Worker, Live-Status per Batch-/hex-Poll,
 * Bearer-Token in localStorage (Single-User-Tool).
 */
export function useWatchlist() {
  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [liveMap, setLiveMap] = useState<Map<string, Aircraft>>(new Map());
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY) ?? '');
  const [error, setError] = useState<string | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const setToken = useCallback((t: string) => {
    setTokenState(t);
    localStorage.setItem(TOKEN_KEY, t);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setItems(await api.watchlist());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Liste initial laden
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Live-Status aller Watchlist-Flieger (ein Batch-Request)
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      const hexes = itemsRef.current.map((i) => i.hex);
      if (hexes.length === 0 || document.hidden) return;
      try {
        const res = await api.hex(hexes);
        if (!cancelled) setLiveMap(new Map(res.ac.map((a) => [a.hex, a])));
      } catch {
        /* nächster Poll versucht es wieder */
      }
    }
    void poll();
    const timer = setInterval(() => void poll(), LIVE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [items.length]);

  const add = useCallback(
    async (hex: string, label: string | null): Promise<string | null> => {
      if (!token) return 'Kein API-Token gesetzt (Feld unten im Watchlist-Panel).';
      try {
        await api.watchlistAdd(hex, label, token);
        await refresh();
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [token, refresh],
  );

  const remove = useCallback(
    async (hex: string): Promise<string | null> => {
      if (!token) return 'Kein API-Token gesetzt.';
      try {
        await api.watchlistRemove(hex, token);
        await refresh();
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [token, refresh],
  );

  const entries: WatchlistEntry[] = items.map((item) => {
    const live = liveMap.get(item.hex) ?? null;
    const status: LiveStatus = !live
      ? 'offline'
      : live.alt_baro === 'ground'
        ? 'ground'
        : 'airborne';
    return { ...item, live, status };
  });

  return { entries, watched: new Set(items.map((i) => i.hex)), add, remove, token, setToken, error };
}
